import { createStore } from 'vuex'
import { RootState, Order, Sender, Payee, ExchangeRate, OrderTypeEnum, SenderTypeEnum } from './types'
import { api } from '../api'
import auth from './modules/auth'

const initialOrderState: Partial<Order> = {
  order_type: OrderTypeEnum.PAY_INVOICE_FOR_GOODS,
  sender: {
    type: SenderTypeEnum.OOO,
    name: null,
    name_en: null,
    inn: null,
    bank_name: null,
    bank_name_en: null,
    bank_bik: null,
    bank_current_account: null,
    bank_correspondent_account: null,
    entity_address: null,
    entity_address_en: null,
    kpp: null,
    ceo_name: null,
    ceo_name_genitive: null,
    ceo_name_en: null,
    cfo_name: null,
    cfo_name_genitive: null,
    cfo_name_en: null,
    signed_agreement_status: null,
    signed_agreement_problem_description: null,
    agreement_document_url: null,
    agreement_number: null,
    agreement_date: null,
  } as Sender
}

export default createStore<RootState>({
  modules: {
    auth
  },

  state: {
    orders: [],
    currentOrder: { ...initialOrderState },
    senders: [],
    payees: [],
    exchangeRates: [],
    loading: false,
    error: null,
    activeOrderStep: 0
  },

  getters: {
    isOrderDetailsValid: (state) => {
      return Boolean(
        state.currentOrder.order_type && 
        state.currentOrder.currency && 
        state.currentOrder.amount && 
        state.currentOrder.amount > 0
      )
    },
    
    isSenderInformationValid: (state) => {
      // If using an existing sender
      if (state.currentOrder.sender_id) {
        return true
      }
      
      // If using a new sender, check required fields
      const sender = state.currentOrder.sender
      if (!sender || !sender.type) {
        return false
      }
      
      // Check common required sender fields
      return Boolean(
        sender.name && 
        sender.inn && 
        sender.bank_name && 
        sender.bank_bik && 
        sender.bank_current_account
      )
    },
    
    isPayeeInformationValid: (state) => {
      // If using an existing payee
      if (state.currentOrder.payee_id) {
        return true
      }
      
      // If using a new payee, check required fields
      const payee = state.currentOrder.payee
      if (!payee) {
        return false
      }
      
      // Check required payee fields
      return Boolean(
        payee.name && 
        payee.account && 
        payee.bank_name && 
        payee.bank_swift
      )
    }
  },

  mutations: {
    SET_ORDERS(state, orders: Order[]) {
      state.orders = orders
    },
    SET_CURRENT_ORDER(state, order: Partial<Order>) {
      state.currentOrder = {
        ...state.currentOrder,
        ...order
      }
    },
    RESET_CURRENT_ORDER(state) {
      state.currentOrder = { ...initialOrderState }
    },
    UPDATE_ORDER_FIELD(state, { field, value }) {
      state.currentOrder = {
        ...state.currentOrder,
        [field]: value
      }
    },
    SET_SENDER_FOR_CURRENT_ORDER(state, sender: Sender) {
      state.currentOrder.sender = {
        ...state.currentOrder.sender,
        ...sender
      }
    },
    SET_PAYEE_FOR_CURRENT_ORDER(state, payee: Payee) {
      state.currentOrder.payee = {
        ...state.currentOrder.payee,
        ...payee
      }
    },
    SET_SENDERS(state, senders: Sender[]) {
      state.senders = senders
    },
    SET_PAYEES(state, payees: Payee[]) {
      state.payees = payees
    },
    SET_EXCHANGE_RATES(state, rates: ExchangeRate[]) {
      state.exchangeRates = rates
    },
    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },
    SET_ERROR(state, error: string | null) {
      state.error = error
    },
    SET_ACTIVE_ORDER_STEP(state, step: number) {
      state.activeOrderStep = step
    }
  },

  actions: {
    async fetchOrders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const orders = await api.orders.fetch()
        commit('SET_ORDERS', orders)
      } catch (error) {
        commit('SET_ERROR', 'Failed to fetch orders')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async loadCurrentOrderByExistingId({ commit }, orderId: string) {
      commit('SET_LOADING', true)
      try {
        const order = await api.orders.fetchById(orderId)
        commit('SET_CURRENT_ORDER', order)
        if (order.sender_id) {
          const sender = await api.senders.fetchById(order.sender_id)
          commit('SET_SENDER_FOR_CURRENT_ORDER', sender)
        }
        if (order.payee_id) {
          const payee = await api.payees.fetchById(order.payee_id)
          commit('SET_PAYEE_FOR_CURRENT_ORDER', payee)
        }
        return order
      } catch (error) {
        commit('SET_ERROR', 'Failed to fetch order')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createOrder({ commit }, order: Order) {
      commit('SET_LOADING', true)
      try {
        const createdOrder = await api.orders.create(order)
        commit('SET_CURRENT_ORDER', createdOrder)
      } catch (error) {
        commit('SET_ERROR', 'Failed to create order')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateOrder({ commit }, order: Partial<Order>) {
      if (!order.id) {
        throw new Error('Order ID is required for update')
      }
      
      commit('SET_LOADING', true)
      try {
        const updatedOrder = await api.orders.update(order.id, order)
        commit('SET_CURRENT_ORDER', updatedOrder)
      } catch (error) {
        commit('SET_ERROR', 'Failed to update order')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchSenders({ commit }) {
      commit('SET_LOADING', true)
      try {
        const senders = await api.senders.fetch()
        commit('SET_SENDERS', senders)
      } catch (error) {
        commit('SET_ERROR', 'Failed to fetch senders')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createSender({ commit, state }, sender: Sender) {
      commit('SET_LOADING', true)
      try {
        const createdSender = await api.senders.create(sender)
        state.senders.push(createdSender)
        commit('SET_SENDER_FOR_CURRENT_ORDER', createdSender)
        return createdSender
      } catch (error) {
        commit('SET_ERROR', 'Failed to create sender')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async setSenderForCurrentOrder({ commit, state }, sender: Sender) {
      commit('SET_LOADING', true)
      try {
        if (!sender.id) {
          throw new Error('Sender ID is required for assignment')
        }
        
        if (!state.currentOrder.id) {
          throw new Error('Order ID is required for sender assignment')
        }
        
        const updatedSender = await api.orders.assignSender(state.currentOrder.id, sender.id)
        commit('SET_SENDER_FOR_CURRENT_ORDER', updatedSender)
        state.currentOrder.sender_id = updatedSender.id
        state.currentOrder.sender_type = updatedSender.type
      } catch (error) {
        commit('SET_ERROR', 'Failed to assign sender to order')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateSender({ commit }, sender: Sender) {
      if (!sender.id) {
        throw new Error('Sender ID is required for update')
      }
      
      commit('SET_LOADING', true)
      try {
        await api.senders.update(sender.id, sender)
      } catch (error) {
        commit('SET_ERROR', 'Failed to update sender')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteSender({ commit, state }, senderId: number) {
      commit('SET_LOADING', true)
      try {
        await api.senders.delete(senderId)
        state.senders = state.senders.filter(sender => sender.id !== senderId)
      } catch (error) {
        commit('SET_ERROR', 'Failed to delete sender')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async parseSenderDocument({ commit, state }, file: File) {
      if (!state.currentOrder.sender?.type) {
        throw new Error('Sender type must be specified before uploading document')
      }
      
      commit('SET_LOADING', true)
      try {
        const senderType = state.currentOrder.sender.type
        const senderData = await api.senders.parseSenderDocument(senderType, file)
        commit('SET_SENDER_FOR_CURRENT_ORDER', senderData)
        return senderData
      } catch (error) {
        commit('SET_ERROR', 'Failed to parse sender document')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchPayees({ commit }) {
      commit('SET_LOADING', true)
      try {
        const payees = await api.payees.fetch()
        commit('SET_PAYEES', payees)
      } catch (error) {
        commit('SET_ERROR', 'Failed to fetch payees')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createPayee({ commit, state }, payee: Payee) {
      commit('SET_LOADING', true)
      try {
        const createdPayee = await api.payees.create(payee)
        state.payees.push(createdPayee)
        commit('SET_PAYEE_FOR_CURRENT_ORDER', createdPayee)
        return createdPayee
      } catch (error) {
        commit('SET_ERROR', 'Failed to create payee')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updatePayee({ commit }, payee: Payee) {
      if (!payee.id) {
        throw new Error('Payee ID is required for update')
      }
      
      commit('SET_LOADING', true)
      try {
        await api.payees.update(payee.id, payee)
      } catch (error) {
        commit('SET_ERROR', 'Failed to update payee')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async parsePayeeDocument({ commit, state }, file: File) {
      commit('SET_LOADING', true)
      try {
        const payeeData = await api.payees.parsePayeeDocument(file)
        commit('SET_PAYEE_FOR_CURRENT_ORDER', payeeData)
        return payeeData
      } catch (error) {
        commit('SET_ERROR', 'Failed to parse payee document')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async setPayeeForCurrentOrder({ commit, state }, payee: Payee) {
      commit('SET_LOADING', true)
      try {
        if (!payee.id) {
          throw new Error('Payee ID is required for assignment')
        }
        
        if (!state.currentOrder.id) {
          throw new Error('Order ID is required for payee assignment')
        }
        
        const updatedOrder = await api.orders.assignPayee(state.currentOrder.id, payee.id)
        commit('SET_CURRENT_ORDER', updatedOrder)
      } catch (error) {
        commit('SET_ERROR', 'Failed to assign payee to order')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchExchangeRates({ commit }) {
      commit('SET_LOADING', true)
      try {
        const rates = await api.exchangeRates.fetch()
        commit('SET_EXCHANGE_RATES', rates)
        return rates
      } catch (error) {
        commit('SET_ERROR', 'Failed to fetch exchange rates')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  }
})
